import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion,AccordionSummary,Pagination,Skeleton,Stack,TextField,Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useQuery } from "@tanstack/react-query";
import MUIDataTable,{ MUIDataTableOptions } from "mui-datatables";
import { useEffect,useState } from 'react';
import { useTranslation } from 'react-i18next';
import AttToken from '../../helpers/attToken';
import { trackEventMatomo } from '../../helpers/matomo';
import QueryFn from "../../helpers/QueryFn";
import Pesquisa,{ perguntasType,pesquisaType,respostasType } from '../../service/Pesquisas';
import usePagination from "../Pagination/Pagination";
import { getMuiTheme } from "../TableHelpers/options";
import { tableOptions } from '../TableHelpers/TableOptions';
import ModalRespostas from './ModalRespostas';


const pesquisaService = new Pesquisa();
const queryFn = new QueryFn();

export default function ResearchesComp() {
  const [expanded, setExpanded] = useState<string>('');
  const [selected, setSelected] = useState<respostasType | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [search, setSearch] = useState<string>('');
  const [paginated, setPaginated] = useState<pesquisaType[]>([]);
  const { t } = useTranslation('translation');
  const [page, setPage] = useState(1);

  const { data, isLoading, isSuccess, isError } = useQuery({
    queryKey: ['GET_RESPONDIDAS'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const response = await pesquisaService.getAll(token);
        return response;
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    staleTime: 50 * 60 * 1000,
  });

  const PER_PAGE = 5;
  const count = Math.ceil(data?.length / PER_PAGE);
  const _DATA = usePagination<pesquisaType>(data?.sort((a: pesquisaType, b: pesquisaType) => { return a.nome > b.nome ? 1 : -1; }), PER_PAGE);

  useEffect(() => {
    if (isSuccess) trackEventMatomo('pesquisas legado', 'sucesso', 'carregamento', 'pesquisas carregadas');
    if (isError) trackEventMatomo('pesquisas legado', 'erro', 'carregamento', 'falha ao carregar pesquisas');
  }, [isError, isSuccess]);

  useEffect(() => {
    search ? setPaginated(data
      ?.filter((e: pesquisaType) => e.nome
        ?.normalize("NFD")
        ?.replace(/[\u0300-\u036f]/g, "")
        ?.toLowerCase()
        ?.includes(search
          ?.normalize("NFD")
          ?.replace(/[\u0300-\u036f]/g, "")
          ?.toLowerCase()))) : setPaginated(_DATA.currentData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page, isLoading]);

  const handleChange = (_e: React.ChangeEvent<unknown>, p: number) => {
    trackEventMatomo('pesquisas legado', 'click', 'paginação', `acessa página ${p}`);
    setPage(p);
    _DATA.jump(p);
  };

  const { data: respostas, isPending, mutate } = queryFn.useMutate('GET_RESPOSTAS1',
    async (value?: string) => {
      try {
        const token = await AttToken();
        if (token) {
          const response = await pesquisaService.getExecutions(token, value!);
          return response;
        }
      } catch (err) {
        console.error(err);
      }
    });

  const columns = [
    {
      name: "inicio",
      options: {
        display: false,
        filter: false,
        sort: true,
        sortThirdClickReset: true,
      }
    },
    {
      name: "inicio",
      label: t('Researches.startedOn'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          return <span>{new Date(value).toLocaleString('pt-BR')}</span>;
        }
      }
    },
    {
      name: "fim",
      label: t('Researches.finishedOn'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: number) => {
          return <span>{new Date(value).toLocaleString('pt-BR')}</span>;
        }
      }
    },
    {
      name: 'id',
      label: t('Researches.duration'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (value: string) => {
          const duracao = respostas?.find((elem: respostasType) => elem.id === value);
          const inicio = new Date(duracao?.inicio).getTime();
          const fim = new Date(duracao?.fim).getTime();
          const hour = new Date(inicio).getHours() - new Date(fim).getHours();
          const diff = Number(fim) - Number(inicio);
          const hora = new Date(diff).toLocaleTimeString();
          const sub = hora.substring(2);
          return <span>{`${hour}` + sub}</span>;
        }
      }
    },
    {
      name: "perguntas",
      label: t('Researches.completion'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (pergunta: perguntasType[]) => {
          const respondida = pergunta.filter((elem) => elem.resposta || elem.resposta === 0).length;
          return (<span>{respondida} {t('table.displayRows')} {pergunta?.length} {t('Researches.ofQuestions')}</span>);
        }
      }
    },
    {
      name: "acerto",
      label: t('Researches.correctAnswerRate'),
      options: {
        filter: false,
        sort: true,
        sortThirdClickReset: true,
        customBodyRender: (acerto: { acertos: number, total: number }) => {
          return (acerto ? <span>{acerto?.acertos} {t('Researches.correctAnswersOf')} {acerto.total} ({(acerto.acertos * 100) / acerto.total}%)</span> : <>{t('Researches.notFound')}</>);
        }
      }
    },
  ];

  const translatedTextLabels: MUIDataTableOptions = {
    onRowClick: (rowData, param) => {
      const value = respostas.find((e: respostasType) => e.inicio === Number(rowData[0]));
      if (value) {
        trackEventMatomo('pesquisas legado', 'click', 'linha', 'abre respostas da pesquisa');
        setSelected(value);
        setOpenModal(!openModal);
      }
    },
    textLabels: {
      body: {
        noMatch: `${t('table.noMatch')}`,
        toolTip: `${t('table.toolTip')}`,
        columnHeaderTooltip: column =>
          `${t('table.columnHeaderTooltip')} ${column.label}`
      },
      pagination: {
        next: `${t('table.next')}`,
        previous: `${t('table.previous')}`,
        rowsPerPage: `${t('table.rowsPerPage')}`,
        displayRows: `${t('table.displayRows')}`
      },
      toolbar: {
        search: `${t('table.search')}`,
        filterTable: `${t('table.filterTable')}`
      },
      filter: {
        title: `${t('table.title')}`,
        reset: `${t('table.reset')}`
      },
      viewColumns: {
        title: `${t('table.viewColumnsTitle')}`
      },
    },
  };

  function Loading() {
    const size = [1, 2, 3, 4, 5];
    return (size.map((_e) => (
      <Skeleton key={Math.random()} variant="rounded" height={60} width={'100%'} />
    )));
  };

  return (
    <>
      {openModal && <ModalRespostas selected={selected as respostasType} openModal={openModal} setOpenModal={setOpenModal} />}
      <Stack
        direction="column"
        justifyContent="center"
        alignItems="flex-start"
        spacing={1}
        width={'100%'}
      >
        {isLoading
          ? <Loading />
          : <>
            {data?.length > 5
              && <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={1}>
                <Pagination
                  boundaryCount={4}
                  showFirstButton
                  showLastButton
                  count={count}
                  size="small"
                  page={page}
                  variant='text'
                  shape='circular'
                  onChange={handleChange}
                />
                <TextField
                  size="small"
                  fullWidth
                  label={t('Researches.search')}
                  value={search}
                  onClick={() => trackEventMatomo('Pesquisa legado', 'click', 'input', 'edita valor')}
                  onChange={(event) => {
                    trackEventMatomo('pesquisas legado', 'click', 'input', 'pesquisa por nome');
                    setSearch(event.target.value.toLowerCase());
                  }}
                  style={{ marginBottom: '10px' }}
                />
              </Stack>}
            {paginated?.map((modelo: pesquisaType) => (
              <Accordion expanded={expanded === modelo.id} style={{ width: '100%' }} key={modelo.id}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon onClick={() => {
                    trackEventMatomo('Pesquisa legado', 'click', 'acordeon', 'abre/fecha');
                    if (expanded === modelo.id) {
                      setExpanded('');
                    } else {
                      setExpanded(modelo.id);
                      trackEventMatomo('pesquisas legado', 'click', 'acordeon', 'carrega respostas da pesquisa');
                      mutate(modelo.id);
                    }
                  }} />}
                >
                  <Stack direction='column' width='100%' justifyContent='flex-start' alignItems='flex-start' spacing={1}>
                    <Typography
                      sx={{
                        color: '#18A689',
                        fontWeight: 600,
                        fontSize: '1.5em'
                      }}>
                      {modelo.nome}
                    </Typography>
                    <Typography
                      component='p'
                      color='textDisabled'
                      fontSize={'13px'}
                      fontWeight={400}
                    >
                      {t('Researches.createdBy')}
                      <Typography fontSize={'13px'} component='span' fontWeight={600}> {modelo.userNome} </Typography>
                      {t('Researches.on')} {new Date(modelo.dataCadastro).toLocaleString()} {t('Researches.version')} {modelo.versao}
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <Stack width='100%' padding={2}>
                  {isPending
                    ? <Skeleton variant="rounded" height={200} width={'100%'} />
                    : <ThemeProvider theme={getMuiTheme()}>
                      <MUIDataTable
                        title={<p style={{ wordBreak: 'break-word' }}>
                          {t('Researches.surveyAnswers')} <b>{modelo.nome}</b>
                        </p>
                        }
                        data={respostas}
                        columns={columns}
                        options={{ ...tableOptions, ...translatedTextLabels }}
                      />
                    </ThemeProvider>}
                </Stack>
              </Accordion>
            ))}
          </>
        }
      </Stack>
    </>
  );
}
